// === Dashboard card access — pure helpers ===
//
// A job title carries an allow-list of dashboard cards (user_titles.visible_cards,
// route paths from config/nav-groups.tsx). Company admins have full access.
// The provider in card-access-context.tsx fetches the signed-in user's access;
// these helpers decide what that means for a given card or URL.

export interface CardAccess {
  readonly fullAccess: boolean;
  readonly visibleCards: readonly string[];
}

/**
 * The card a URL belongs to, or null when it isn't a card page (dashboard,
 * to-do list, …). `/general-tasks/12/edit` belongs to `/general-tasks`.
 *
 * Lower-cased first: React Router matches routes case-insensitively, so
 * `/ML-Hours` still renders the ML Hours page and must still be guarded.
 */
export function matchCardPath(pathname: string, cardPaths: readonly string[]): string | null {
  const path = pathname.toLowerCase();
  let match: string | null = null;
  for (const cardPath of cardPaths) {
    if (path === cardPath || path.startsWith(`${cardPath}/`)) {
      // Longest wins, so a nested card can never be shadowed by its parent.
      if (match === null || cardPath.length > match.length) match = cardPath;
    }
  }
  return match;
}

export function canViewCard(access: CardAccess, cardPath: string): boolean {
  return access.fullAccess || access.visibleCards.includes(cardPath);
}

/**
 * Keep only cards that exist today, de-duplicated, in dashboard order — so a
 * card removed from the app is dropped on the next save instead of lingering
 * and silently coming back if a card with that path is ever re-added.
 */
export function sanitiseVisibleCards(selected: readonly string[], cardPaths: readonly string[]): string[] {
  const chosen = new Set(selected);
  return cardPaths.filter((cardPath) => chosen.has(cardPath));
}
